import { isEmbeddedInApp } from "@/lib/native-bridge";
import {
  encodeFirstTouch,
  FIRST_TOUCH_COOKIE,
  FIRST_TOUCH_MAX_AGE_S,
  type FirstTouch,
} from "@shared/first-touch";

/**
 * Record the visitor's FIRST visit (BDAY-195): utm tags, the referring site (host only), the
 * landing path and ad click ids, in a first-party cookie the server stamps onto the account at
 * signup (server/lib/acquisition.ts). Runs once at startup; never overwrites an existing touch.
 *
 * SameSite=None on https so Apple's cross-site form_post callback still carries it (a Lax
 * cookie is withheld there and every Apple web signup would read as "direct").
 */
export function captureFirstTouch(): void {
  try {
    if (typeof document === "undefined") return;
    if (document.cookie.split("; ").some((c) => c.startsWith(`${FIRST_TOUCH_COOKIE}=`))) return;
    // Inside the app's webview the app attributes its own signups (app:<platform>).
    if (isEmbeddedInApp()) return;

    const url = new URL(window.location.href);
    const q = url.searchParams;
    const get = (k: string) => q.get(k)?.trim().slice(0, 200) || undefined;
    let ref: string | undefined;
    try {
      const host = document.referrer ? new URL(document.referrer).host : "";
      if (host && host !== window.location.host) ref = host.slice(0, 200);
    } catch {
      /* unparsable referrer → none */
    }
    const now = Date.now();
    const fbclid = get("fbclid");
    const ft: FirstTouch = {
      p: url.pathname.slice(0, 200) || "/",
      r: ref,
      us: get("utm_source"),
      um: get("utm_medium"),
      uc: get("utm_campaign"),
      uct: get("utm_content"),
      ut: get("utm_term"),
      // Meta's documented fbc shape: fb.<subdomain index>.<creation ms>.<fbclid>.
      fbc: fbclid ? `fb.1.${now}.${fbclid}` : undefined,
      gclid: get("gclid"),
      gbraid: get("gbraid"),
      wbraid: get("wbraid"),
      ts: now,
    };
    const secure = window.location.protocol === "https:";
    document.cookie =
      `${FIRST_TOUCH_COOKIE}=${encodeFirstTouch(ft)}; Path=/; Max-Age=${FIRST_TOUCH_MAX_AGE_S}` +
      (secure ? "; SameSite=None; Secure" : "; SameSite=Lax");
  } catch {
    /* attribution is best-effort */
  }
}
